import { useState } from "react";
import { CheckCircle2, ChevronDown, Send } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LEVELS = ["silver", "golden"];
const EMPTY = { first_name: "", last_name: "", phone: "", email: "", level: "" };

export default function RegistrationSection() {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/registrations", form);
      setDone(form);
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || t("reg_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="contact" data-testid="registration-section" className="relative py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold tracking-widest uppercase text-sunset mb-3">{t("reg_label")}</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl text-slate-900 leading-tight">
              {t("reg_title_a")}
              <br />
              <span className="text-ocean">{t("reg_title_b")}</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{t("reg_subtitle")}</p>
            <ul className="mt-8 space-y-3">
              {LEVELS.map((lv) => (
                <li key={lv} className="flex items-start gap-3">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-sunset shrink-0" />
                  <div>
                    <p className="font-bold text-slate-900">{t(`reg_level_${lv}`)}</p>
                    <p className="text-sm text-slate-500">{t(`reg_level_${lv}_desc`)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-sand/60 border border-slate-100 shadow-soft p-6 sm:p-10">
              {done ? (
                <div className="text-center py-8" data-testid="registration-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                    <CheckCircle2 className="h-9 w-9" />
                  </div>
                  <h3 className="mt-5 font-heading font-black text-2xl sm:text-3xl text-slate-900">
                    {t("reg_success_title")}, {done.first_name}!
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">{t("reg_success_body")}</p>
                  <button
                    type="button"
                    onClick={() => setDone(null)}
                    data-testid="registration-again-btn"
                    className="mt-8 px-6 py-3 rounded-full bg-white border border-slate-200 font-semibold text-slate-700 hover:border-ocean"
                  >
                    {t("reg_another")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="registration-form">
                  <Field label={t("reg_first_name")}>
                    <Input required value={form.first_name} onChange={set("first_name")} data-testid="reg-first-name-input" className="h-12 rounded-xl bg-white" />
                  </Field>
                  <Field label={t("reg_last_name")}>
                    <Input required value={form.last_name} onChange={set("last_name")} data-testid="reg-last-name-input" className="h-12 rounded-xl bg-white" />
                  </Field>
                  <Field label={t("reg_phone")}>
                    <Input required type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="reg-phone-input" className="h-12 rounded-xl bg-white" />
                  </Field>
                  <Field label={t("reg_email")}>
                    <Input required type="email" value={form.email} onChange={set("email")} data-testid="reg-email-input" className="h-12 rounded-xl bg-white" />
                  </Field>
                  <Field label={t("reg_level")} className="sm:col-span-2">
                    <div className="relative">
                      <select
                        required
                        value={form.level}
                        onChange={set("level")}
                        data-testid="reg-level-select"
                        className="h-12 w-full appearance-none rounded-xl border border-input bg-white px-3 pr-10 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-ocean/40"
                      >
                        <option value="" disabled>{t("reg_level_placeholder")}</option>
                        {LEVELS.map((lv) => (
                          <option key={lv} value={lv}>{t(`reg_level_${lv}`)}</option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    </div>
                  </Field>
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="reg-submit-btn"
                    className="sm:col-span-2 inline-flex items-center justify-center gap-2 h-14 rounded-full bg-sunset text-white font-bold text-lg hover:bg-sunset-hover transition-all shadow-sunset disabled:opacity-60"
                  >
                    <Send className="h-5 w-5" />
                    {saving ? t("reg_sending") : t("reg_submit")}
                  </button>
                  <p className="sm:col-span-2 text-xs text-slate-500 text-center">{t("reg_privacy")}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
